import { trackUsage } from '@creditsights/api-ui-web';
import { DataLoadError, Divider, Loader } from '@creditsights/styleguide-web';
import classNames from 'classnames/bind';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '../../Base.constants';
import ArticlePreview from '../../components/ArticlePreview';
import {
  getArticlePreviewHandler,
  OpenArticlePreviewOnClickKey,
} from '../../components/ArticlePreview/ArticlePreview.logic';
import LegalCompliance from '../../components/widgets/LegalCompliance';
import { useFetchArticle, usePrepareArticleContent } from '../../hooks/articles';
import useArticleLocationState from '../../hooks/articles/useArticleLocationState';
import useCheckArticleAccessStatus from '../../hooks/articles/useCheckArticleAccessStatus';
import { useBreakpoint, useDocumentTitle, useLocalStorage } from '../../hooks/common';
import { useFetchForPrint } from '../../hooks/print';
import { useAddRecentlyViewed } from '../../hooks/recentlyViewed';
import { Article as ArticleModel } from '../../models';
import { AccessStatus } from '../../services/dtos/article/article.dto';
import { isNotFound, shouldNavigate } from '../../utils/navigationUtils';

import {
  extractIssuerIds,
  extractMultipartTag,
  getMorningCommentRegion,
  getRelatedTopics,
  getSecondaryCompanyIds,
} from '../../utils/tagUtils';
import NotFound from '../NotFound';
import { getArticleComponents, isSectorReview, overrideArticleComponents } from './Article.logic';
import styles from './Article.module.scss';
import ArticleContent from './ArticleContent';
import ArticleHeader from './ArticleHeader';
import ArticleIncludedContent from './ArticleIncludedContent/ArticleIncludedContent';
import ArticlePrintView from './ArticlePrintView';
import ArticleRightContainer from './ArticleRightContainer';
import RestrictedArticleView from '../../components/RestrictedArticleAccess/RestrictedArticleView';

const cx = classNames.bind(styles);

interface ArticleProps {
  isCompact?: boolean;
  isArticleListLoading?: boolean;
}

const Article = ({ isCompact, isArticleListLoading }: ArticleProps): React.ReactElement => {
  const articlePrintRef = useRef<HTMLDivElement>(null);
  const { articleId } = useParams<string>();
  const navigate = useNavigate();

  const { checkQuota, checkLoanWaiver } = useArticleLocationState();
  const [confirmQuotaUse, setConfirmQuotaUse] = useState(!checkQuota);
  const [confirmLoanWaiver, setConfirmLoanWaiver] = useState(!checkLoanWaiver);

  const {
    data: article,
    isLoading: isArticleLoading,
    isRefetching: isArticleRefetching,
    isError,
    refetch,
    error,
  } = useFetchArticle(Number(articleId), confirmQuotaUse, confirmLoanWaiver);

  useEffect(() => {
    if (checkQuota && confirmQuotaUse) {
      setConfirmQuotaUse(false);
    }
  }, [confirmQuotaUse]);

  useEffect(() => {
    if (checkLoanWaiver && confirmLoanWaiver) {
      setConfirmLoanWaiver(false);
    }
  }, [confirmLoanWaiver]);

  const {
    data: articleContent,
    isLoading: isArticleContentLoading,
    isRefetching: isArticleContentRefetching,
  } = usePrepareArticleContent(article);
  const morningCommentRegion = useMemo(() => getMorningCommentRegion(article?.tags), [article]);

  const [imagesLoaded, setImagesLoaded] = useState(false);
  const { userFullName, disclaimer, redistributionAllowed, isContentReadyForPrint } = useFetchForPrint(imagesLoaded);
  useDocumentTitle(article?.content.title);

  const { isTabletPortrait, isDesktop } = useBreakpoint();
  const addRecentlyViewed = useAddRecentlyViewed();

  const [previewedArticleId, setPreviewedArticleId] = useState<number | null>(null);
  const showArticlePreview = !!(isDesktop && previewedArticleId);
  const [openArticlePreviewOnClick] = useLocalStorage<boolean>(OpenArticlePreviewOnClickKey, true);
  const checkArticleAccessStatus = useCheckArticleAccessStatus();

  const onPreviewableArticleClick = getArticlePreviewHandler(
    setPreviewedArticleId,
    isDesktop,
    navigate,
    checkArticleAccessStatus,
    openArticlePreviewOnClick,
    isCompact
  );

  const [isAnalystWidgetLoading, setIsAnalystWidgetLoading] = useState<boolean>(false);
  const isLoading =
    isArticleLoading ||
    isArticleRefetching ||
    !!isArticleListLoading ||
    isArticleContentLoading ||
    isArticleContentRefetching;

  useEffect(() => {
    if (articleContent && articleContent.canAccess) {
      addRecentlyViewed.mutate(articleContent);
    }
  }, [articleContent]);

  useEffect(() => {
    setPreviewedArticleId(null);

    if (articleId) {
      trackUsage('ARTICLE_VIEW', [
        {
          key: 'articleId',
          value: `${articleId}`,
        },
        {
          key: 'viewMode',
          value: 'full',
        },
      ]);
    }
  }, [articleId]);

  const { renderableComponents, relatedComponents: relatedDocuments } = getArticleComponents(
    articleContent?.content.components || [],
    articleContent?.articleType
  );

  const renderableArticle = useMemo(
    () => (articleContent ? overrideArticleComponents(articleContent, renderableComponents) : undefined),
    [articleContent]
  );

  useEffect(() => {
    if (!isLoading && isNotFound(error) && !article && !isCompact) {
      navigate(ROUTES.notFound, { replace: true });
    }
  }, [article, isLoading, error, isCompact]);

  const renderArticle = (article: ArticleModel | undefined) => {
    const relatedTopics = article ? getRelatedTopics(article.tags) : [];
    const focusCompanyTags: string[] = article ? extractIssuerIds(article.focusTags) : [];
    const companyTags: string[] = article ? extractIssuerIds(article.tags) : [];
    const secondaryCompanyTags: string[] = getSecondaryCompanyIds(focusCompanyTags, companyTags);
    const multipartTag = article ? extractMultipartTag(article?.tags) : undefined;

    if (!article) {
      return <NotFound showNavigateToFeeds={!isCompact} />;
    }

    const { content, analysts, articleType, canAccess, accessStatus } = article;
    const { includedContent } = content;
    const isPreviewable = !shouldNavigate(isDesktop, openArticlePreviewOnClick, isCompact);
    const isSectorReviewArticle = isSectorReview(article);
    const legalComplianceContainer = (
      <div className={cx('legal-compliance-wrapper')}>
        {isCompact && <Divider />}
        <LegalCompliance dataTestId="article-related-compliance-link" />
      </div>
    );
    const canRenderArticle = accessStatus === AccessStatus.granted;

    return (
      <div className={cx('main', { 'is-expanded': !canRenderArticle })}>
        <div className={cx('header', { 'tab-header': isCompact, 'can-access': canAccess })}>
          <ArticleHeader
            isTabletPortrait={isTabletPortrait}
            isDesktop={isDesktop}
            isCompact={isCompact}
            article={article}
            isMorningComment={!!morningCommentRegion}
            isContentReadyForPrint={isContentReadyForPrint}
            articlePrintRef={articlePrintRef}
            askAnalystModalSubject={{ articleId: article.id, subjectName: article.content.title }}
            isAnalystWidgetLoading={isAnalystWidgetLoading}
            setIsArticleAnalystWidgetLoading={setIsAnalystWidgetLoading}
          />
        </div>
        {canRenderArticle ? (
          <>
            <div className={cx('content')} data-testid="article-parent-container">
              <div
                className={cx('article-left-container', { 'tab-left-container': isCompact })}
                data-testid="article-left-container"
              >
                <ArticleContent
                  content={content}
                  isCompact={isCompact}
                  setImagesLoaded={setImagesLoaded}
                  onArticleClick={onPreviewableArticleClick}
                  isSectorReview={isSectorReviewArticle}
                />
                <ArticleIncludedContent
                  includedContent={includedContent}
                  isCompact={isCompact}
                  onArticleClick={isSectorReviewArticle && !isDesktop ? undefined : onPreviewableArticleClick}
                  isPreviewable={isPreviewable}
                  previewedArticleId={previewedArticleId}
                  isSectorReview={isSectorReviewArticle}
                />
                {!isTabletPortrait && legalComplianceContainer}
              </div>
              {!isCompact && (
                <ArticleRightContainer
                  relatedDocuments={relatedDocuments}
                  multipartTag={multipartTag}
                  analysts={analysts}
                  focusCompanyTags={focusCompanyTags}
                  secondaryCompanyTags={secondaryCompanyTags}
                  relatedTopics={relatedTopics}
                  morningCommentRegion={morningCommentRegion}
                  isArticlePreviewOpen={showArticlePreview}
                  isTabletPortrait={isTabletPortrait}
                  contentType={articleType}
                  askAnalystModalSubject={{ articleId: article.id, subjectName: article.content.title }}
                  setIsAnalystWidgetLoading={setIsAnalystWidgetLoading}
                />
              )}
            </div>
            {isTabletPortrait && legalComplianceContainer}
          </>
        ) : (
          <RestrictedArticleView
            articlePermissions={article}
            setConfirmQuotaUse={setConfirmQuotaUse}
            setConfirmLoanWaiver={setConfirmLoanWaiver}
            isCompact={isCompact}
          />
        )}
      </div>
    );
  };

  if (isError && !isNotFound(error)) {
    return (
      <div className={cx('body', { 'article-tab-body': isCompact })}>
        <div className={cx('error-container')}>
          <DataLoadError onRetryClick={refetch} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div data-testid="article-body" className={cx('body', { 'article-tab-body': isCompact })}>
        {isLoading ? (
          <div className={cx('loader-container')}>
            <Loader position="center" />
          </div>
        ) : (
          renderArticle(renderableArticle)
        )}
        {previewedArticleId && (
          <ArticlePreview
            articleId={previewedArticleId}
            setArticleId={setPreviewedArticleId}
            show={showArticlePreview}
            isCompact={isCompact}
          />
        )}
      </div>
      {renderableArticle && (
        <ArticlePrintView
          ref={articlePrintRef}
          article={renderableArticle}
          userFullName={userFullName}
          disclaimer={disclaimer}
          redistributionAllowed={redistributionAllowed}
          isArticlePreview={false}
          setIsAnalystWidgetLoading={setIsAnalystWidgetLoading}
        />
      )}
    </>
  );
};

export default Article;
